import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import CategoryGrid from './components/CategoryGrid';
import GuidesSection from './components/GuidesSection';
import PillarArticlesSection from './components/PillarArticlesSection';
import StartHereSection from './components/StartHereSection';
import FaqSection from './components/FaqSection';
import { useSeo } from '@/hooks/useSeo';

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  useSeo();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    }
  }, [location.hash]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: Array.from({ length: 8 }, (_, i) => i + 1).map((i) => ({
      '@type': 'Question',
      name: t(`faq.q${i}`),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(`faq.a${i}`),
      },
    })),
  };

  return (
    <>
      <Navbar />
      <main>
        <Hero />

        <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
                {t('what.title')}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-foreground-600 md:text-lg">
                {t('what.body')}
              </p>
              <Link
                to="/usfans-spreadsheet"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                {t('what.cta')}
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base leading-none"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=A%20pair%20of%20cream%20canvas%20sneakers%20beside%20a%20folded%20chunky%20knit%20sweater%20on%20a%20soft%20warm%20sand%20colored%20surface%20with%20gentle%20natural%20light%2C%20editorial%20product%20photography%2C%20high%20detail%2C%20cozy%20neutral%20aesthetic&width=600&height=600&seq=what-1&orientation=squarish&nocache=true"
                alt="USFans Spreadsheet products"
                title="USFans Spreadsheet products"
                className="h-40 w-full rounded-lg object-cover object-top md:h-48"
              />
              <img
                src="https://readdy.ai/api/search-image?query=A%20folded%20denim%20jacket%20and%20a%20small%20leather%20bag%20arranged%20on%20a%20soft%20peach%20colored%20studio%20surface%20with%20soft%20diffused%20daylight%2C%20minimal%20fashion%20editorial%20product%20photography%2C%20high%20detail%2C%20fresh%20modern%20mood&width=600&height=600&seq=what-2&orientation=squarish&nocache=true"
                alt="USFans Spreadsheet categories"
                title="USFans Spreadsheet categories"
                className="h-40 w-full rounded-lg object-cover object-top md:h-48"
              />
            </div>
          </div>
        </section>

        <CategoryGrid />

        <GuidesSection />

        <PillarArticlesSection />

        <StartHereSection />

        <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              {t('teaser.title')}
            </h2>
            <p className="mt-4 text-base text-foreground-600">{t('teaser.subtitle')}</p>
          </div>

          <Link
            to="/usfans-spreadsheet"
            className="mx-auto mt-12 flex max-w-3xl flex-col gap-6 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-0.5 hover:border-primary-300 sm:flex-row cursor-pointer"
          >
            <div className="relative h-48 w-full flex-none sm:h-auto sm:w-64">
              <img
                src="https://readdy.ai/api/search-image?query=A%20clean%20desk%20scene%20with%20a%20laptop%20showing%20a%20spreadsheet%2C%20a%20folded%20t-shirt%20and%20a%20notebook%20arranged%20under%20warm%20natural%20light%20on%20a%20soft%20oat%20colored%20surface%2C%20editorial%20lifestyle%20photography%2C%20high%20detail%2C%20organized%20and%20inviting&width=600&height=600&seq=teaser-1&orientation=squarish&nocache=true"
                alt={t('teaser.cardTitle')}
                title={`${t('teaser.cardTitle')} guide`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="flex flex-col justify-center p-6">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800 whitespace-nowrap">
                  {t('teaser.category')}
                </span>
                <span className="text-xs text-foreground-500">{t('teaser.date')}</span>
              </div>
              <h3 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950">
                {t('teaser.cardTitle')}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                {t('teaser.cardDesc')}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
                {t('cta.readArticle')}
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base leading-none"></i>
                </span>
              </span>
            </div>
          </Link>
        </section>

        <section id="safety" className="scroll-mt-24 bg-accent-50">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center md:px-6 md:py-20">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-background-50 mx-auto">
              <i className="ri-shield-check-line text-2xl leading-none"></i>
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              {t('safety.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground-700">
              {t('safety.body')}
            </p>
            <Link
              to="/usfans-spreadsheet"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-accent-700 hover:text-accent-800 cursor-pointer"
            >
              {t('safety.cta')}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-base leading-none"></i>
              </span>
            </Link>
          </div>
        </section>

        <FaqSection />

        <section className="bg-primary-900">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center md:px-6 md:py-20">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-background-50 md:text-4xl">
              {t('finalCta.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-background-200">
              {t('finalCta.subtitle')}
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-background-50 px-8 py-3.5 text-sm font-semibold text-primary-900 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              {t('finalCta.button')}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-up-line text-base leading-none"></i>
              </span>
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}